<script setup>
import Avatar from 'next/avatar/Avatar.vue';
import { ref, computed, watch, nextTick, onMounted } from 'vue';
import { useKeyboardNavigableList } from 'dashboard/composables/useKeyboardNavigableList';
import ConversationApi from 'dashboard/api/conversations';

const props = defineProps({
  searchKey: {
    type: String,
    default: '',
  },
  conversationId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['selectParticipant']);

const participants = ref([]);
const isLoading = ref(true);

const fetchParticipants = async () => {
  try {
    const { data } = await ConversationApi.fetchGroupParticipants(
      props.conversationId
    );
    participants.value = data.participants || [];
  } catch (error) {
    //
  } finally {
    isLoading.value = false;
  }
};

onMounted(fetchParticipants);

const tagParticipantsRef = ref(null);
const selectedIndex = ref(0);

const items = computed(() => {
  const search = props.searchKey?.trim().toLowerCase() || '';

  return participants.value
    .map(p => ({
      id: p.phone,
      displayName: p.name,
      displayInfo: p.phone ? `+${p.phone}` : '',
    }))
    .filter(item =>
      search
        ? item.displayName.toLowerCase().includes(search) ||
          item.displayInfo.includes(search)
        : true
    );
});

const selectableItems = computed(() => {
  return items.value;
});

const getSelectableIndex = item => {
  return selectableItems.value.findIndex(
    selectableItem => selectableItem.id === item.id
  );
};

const adjustScroll = () => {
  nextTick(() => {
    if (tagParticipantsRef.value) {
      const selectedElement = tagParticipantsRef.value.querySelector(
        `#mention-item-${selectedIndex.value}`
      );
      if (selectedElement) {
        selectedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'auto',
        });
      }
    }
  });
};

const onSelect = () => {
  emit('selectParticipant', selectableItems.value[selectedIndex.value]);
};

useKeyboardNavigableList({
  items: selectableItems,
  onSelect,
  adjustScroll,
  selectedIndex,
});

watch(selectableItems, newListOfParticipants => {
  if (newListOfParticipants.length < selectedIndex.value + 1) {
    selectedIndex.value = 0;
  }
});

const onHover = index => {
  selectedIndex.value = index;
};

const onParticipantSelect = index => {
  selectedIndex.value = index;
  onSelect();
};
</script>

<template>
  <div>
    <ul
      v-if="items.length"
      ref="tagParticipantsRef"
      class="vertical dropdown menu mention--box bg-n-solid-1 p-1 rounded-xl text-sm overflow-auto absolute w-full z-20 shadow-md left-0 leading-[1.2] bottom-full max-h-[12.5rem] border border-solid border-n-strong"
      role="listbox"
    >
      <li
        v-for="item in items"
        :id="`mention-item-${getSelectableIndex(item)}`"
        :key="item.id"
      >
        <div
          :class="{
            'bg-n-alpha-black2': getSelectableIndex(item) === selectedIndex,
          }"
          class="flex items-center px-2 py-1 rounded-md cursor-pointer"
          role="option"
          @click="onParticipantSelect(getSelectableIndex(item))"
          @mouseover="onHover(getSelectableIndex(item))"
        >
          <div class="ltr:mr-2 rtl:ml-2">
            <Avatar :name="item.displayName" rounded-full :size="24" />
          </div>
          <div
            class="overflow-hidden flex-1 max-w-full whitespace-nowrap text-ellipsis"
          >
            <h5
              class="overflow-hidden mb-0 text-sm capitalize whitespace-nowrap text-n-slate-11 text-ellipsis"
              :class="{
                'text-n-slate-12': getSelectableIndex(item) === selectedIndex,
              }"
            >
              {{ item.displayName }}
            </h5>
            <div
              class="overflow-hidden text-xs whitespace-nowrap text-ellipsis text-n-slate-10"
              :class="{
                'text-n-slate-11': getSelectableIndex(item) === selectedIndex,
              }"
            >
              {{ item.displayInfo }}
            </div>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>
